import { z } from 'zod';
import { campoActivoSchema } from './campos-activo.js';
import {
  CRITICIDADES,
  ESTADOS_ACTIVO,
  ESTADOS_OT,
  ESTADOS_SOLICITUD,
  PRIORIDADES,
  RECURRENCIAS,
  TIPOS_ACCESO,
  TIPOS_CAMPO_PERSONALIZADO,
  TIPOS_GRUPO,
  TIPOS_TRABAJO,
  ENTIDADES_EXTENSIBLES,
  CATALOGOS,
} from './enums.js';

/**
 * Contratos de la API. Se definen una sola vez y los consumen ambos lados: el
 * servidor para validar la entrada y el cliente para tipar los formularios.
 */

export const uuid = z.string().uuid('Identificador inválido');
const texto = (max: number) => z.string().trim().min(1, 'Requerido').max(max);
/**
 * Fecha de calendario (sin hora) para una columna `date`. A propósito NO usa
 * `z.coerce.date()`: convertir a un `Date` de JS y de vuelta corre la fecha un
 * día por la conversión de huso horario del proceso al guardar (ver
 * `reprogramarOtSchema`/`observacionActivoSchema`, donde se evitó puntualmente
 * antes de generalizarse acá). El texto AAAA-MM-DD llega tal cual y Postgres
 * lo toma como literal, sin pasar por ningún reloj.
 */
export const fechaLiteral = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida (use AAAA-MM-DD)');
const textoOpcional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

// ─── Autenticación ────────────────────────────────────────────────────────────

export const credencialesSchema = z.object({
  email: z.string().trim().toLowerCase().email('Correo inválido'),
  contrasena: z.string().min(1, 'Requerido'),
  /** Slug del tenant; también puede llegar por subdominio. */
  tenant: z
    .string()
    .trim()
    .regex(/^[a-z0-9-]{2,50}$/, 'Organización inválida')
    .optional(),
});
export type Credenciales = z.infer<typeof credencialesSchema>;

/**
 * Política de contraseñas. La longitud es el factor que más aporta; se exige
 * mezcla de clases para frenar las contraseñas de diccionario cortas.
 */
export const contrasenaSchema = z
  .string()
  .min(12, 'Debe tener al menos 12 caracteres')
  .max(128, 'No puede exceder 128 caracteres')
  .refine((v) => /[a-z]/.test(v), 'Debe incluir una minúscula')
  .refine((v) => /[A-Z]/.test(v), 'Debe incluir una mayúscula')
  .refine((v) => /[0-9]/.test(v), 'Debe incluir un número');

export const aceptarInvitacionSchema = z.object({
  token: z.string().min(20),
  contrasena: contrasenaSchema,
});

export const cambiarContrasenaSchema = z.object({
  contrasenaActual: z.string().min(1, 'Requerido'),
  contrasenaNueva: contrasenaSchema,
});

// ─── Paginación y filtros comunes ─────────────────────────────────────────────

export const paginacionSchema = z.object({
  pagina: z.coerce.number().int().min(1).default(1),
  /** Los tamaños replican el selector de la vista de tabla de activos (§5.2). */
  tamano: z.coerce.number().int().refine((v) => [25, 50, 75, 100].includes(v), 'Tamaño inválido').default(25),
  orden: z.string().max(50).optional(),
  direccion: z.enum(['asc', 'desc']).default('asc'),
  buscar: z.string().trim().max(200).optional(),
  incluirEliminados: z.coerce.boolean().default(false),
});
export type Paginacion = z.infer<typeof paginacionSchema>;

export interface Pagina<T> {
  datos: T[];
  total: number;
  pagina: number;
  tamano: number;
}

// ─── Ubicaciones (§4.3) ───────────────────────────────────────────────────────

export const ubicacionSchema = z.object({
  nombre: texto(200),
  ubicacionPadreId: uuid.nullish(),
  tipoUbicacionId: uuid.nullish(),
  direccion: textoOpcional(300),
  descripcion: textoOpcional(4000),
  codigoQr: textoOpcional(40),
  gruposACargo: z.array(uuid).max(50).default([]),
  adjuntos: z.array(uuid).max(20).default([]),
});
export type UbicacionEntrada = z.infer<typeof ubicacionSchema>;

// ─── Tipos de activo ──────────────────────────────────────────────────────────

/** Grupo de activos (hoja del Excel de activos) y los tipos que le pertenecen. */
export const grupoActivoSchema = z.object({
  nombre: texto(120),
  tiposIds: z.array(uuid).max(500).default([]),
});
export type GrupoActivoEntrada = z.infer<typeof grupoActivoSchema>;

export const tipoActivoSchema = z.object({
  nombre: texto(150),
  descripcion: textoOpcional(2000),
  // §9.4b: fallas típicas de este tipo de activo, para que al escanear un QR
  // se pueda ofrecer un desplegable de fallas en vez de un texto libre.
  tiposFallaIds: z.array(uuid).max(200).default([]),
  /** Campos que pide este tipo al registrar un activo, y cuáles son obligatorios. */
  campos: z
    .array(z.object({ campo: campoActivoSchema, obligatorio: z.boolean().default(false) }))
    .max(50)
    .default([]),
});

/** Checklist base: ítems de texto que se copian a cada ubicación nueva de este tipo. */
export const tipoUbicacionSchema = z.object({
  nombre: texto(150),
  descripcion: textoOpcional(2000),
  checklistBase: z.array(z.string().trim().min(1).max(300)).max(100).default([]),
});

// ─── Activos (§5.3) ───────────────────────────────────────────────────────────

export const activoSchema = z
  .object({
    nombre: texto(250),
    tiposActivo: z.array(uuid).max(1).default([]),
    ubicacionId: uuid.nullish(),
    activoPadreId: uuid.nullish(),
    criticidad: z.enum(CRITICIDADES),
    estado: z.enum(ESTADOS_ACTIVO).default('EN_LINEA'),
    motivoInactividadId: uuid.nullish(),
    descripcion: textoOpcional(4000),
    fabricanteId: uuid.nullish(),
    modeloId: uuid.nullish(),
    numeroSerie: textoOpcional(100),
    codigoQr: textoOpcional(40),
    expiracionGarantia: z.coerce.date().nullish(),
    adjuntos: z.array(uuid).max(20).default([]),
    /** Valores de los campos del tipo, por código de campo. Un valor vacío borra el campo. */
    atributos: z.record(z.string(), z.string().max(300).nullable()).optional(),
  })
  // §5.4: el motivo explica por qué el activo está detenido; sin él, el tiempo
  // fuera de línea no se puede clasificar en planificado y no planificado.
  .refine((v) => v.estado !== 'FUERA_DE_LINEA' || !!v.motivoInactividadId, {
    message: 'Un activo fuera de línea requiere un motivo de inactividad',
    path: ['motivoInactividadId'],
  });
export type ActivoEntrada = z.infer<typeof activoSchema>;

/** Activo momentáneo (§ pedido): alta mínima para una OT sin activo real, p. ej. "limpieza de ventanas". */
export const activoMomentaneoSchema = z.object({
  nombre: texto(250),
  ubicacionId: uuid.nullish(),
});
export type ActivoMomentaneoEntrada = z.infer<typeof activoMomentaneoSchema>;

export const cambioEstadoActivoSchema = z
  .object({
    estado: z.enum(ESTADOS_ACTIVO),
    motivoInactividadId: uuid.nullish(),
    nota: textoOpcional(1000),
  })
  .refine((v) => v.estado !== 'FUERA_DE_LINEA' || !!v.motivoInactividadId, {
    message: 'Un activo fuera de línea requiere un motivo de inactividad',
    path: ['motivoInactividadId'],
  });

/** Asignar un activo a una persona de Personal. La nota es opcional (p. ej. «préstamo por obra»). */
export const asignarActivoSchema = z.object({
  personaId: uuid,
  nota: textoOpcional(500),
});
export type AsignarActivoEntrada = z.infer<typeof asignarActivoSchema>;

/**
 * «Deuda técnica»: algo que quedó pendiente en el activo al cerrar una OT (§16.3).
 *
 * `fechaVencimiento` es opcional a propósito NO usa `z.coerce.date()`: igual
 * que en `reprogramarOtSchema`, convertir a un `Date` de JS y de vuelta corre
 * la fecha un día por la conversión de huso horario al guardar en una
 * columna `date`. El texto AAAA-MM-DD llega tal cual y Postgres lo toma como
 * literal.
 */
export const observacionActivoSchema = z.object({
  texto: texto(4000),
  ordenTrabajoId: uuid.nullish(),
  fechaVencimiento: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida (use AAAA-MM-DD)')
    .nullish(),
  adjuntos: z.array(uuid).max(20).default([]),
});
export type ObservacionActivoEntrada = z.infer<typeof observacionActivoSchema>;

export const resolverObservacionSchema = z.object({ resuelta: z.boolean() });

/** Vincula una observación a la OT de seguimiento creada a partir de ella (§16.3). */
export const vincularOtObservacionSchema = z.object({ ordenTrabajoId: uuid });

/** Ítem del checklist de verificadores de una ubicación (§11): a mano, por ubicación puntual. */
export const checklistUbicacionItemSchema = z.object({
  texto: texto(300),
  cumple: z.boolean().default(false),
  activo: z.boolean().default(true),
});
export type ChecklistUbicacionItemEntrada = z.infer<typeof checklistUbicacionItemSchema>;

/**
 * Rango de fechas para los reportes (§15): por defecto, el mes en curso.
 *
 * A propósito literales AAAA-MM-DD y no `z.coerce.date()` (mismo motivo que
 * `fechaLiteral`): el servicio decide el límite superior inclusive/exclusivo
 * y el timezone de comparación, en vez de que ese round-trip por `Date` de JS
 * ya lo corra un día antes de llegar a la query (§ bug reportado).
 */
export const TIPOS_TRABAJO_REPORTE = ['REACTIVA', 'PREVENTIVO', 'OTRO'] as const;

/**
 * Filtros comunes a todos los reportes. Cada uno es opcional y se combina con
 * los demás: una sede (con sus consultorios), un tipo de activo, una criticidad,
 * un técnico o un tipo de trabajo. Página y PDF usan el mismo filtro.
 */
/** Lista separada por comas en la URL (`?sede=a,b`) o arreglo ya parseado. */
const listaDe = <T extends z.ZodTypeAny>(elemento: T) =>
  z.preprocess((v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v), z.array(elemento).optional());

export const filtroReporteSchema = z.object({
  fechaDesde: fechaLiteral.optional(),
  fechaHasta: fechaLiteral.optional(),
  ubicacionId: listaDe(uuid),
  tipoActivoId: listaDe(uuid),
  criticidad: listaDe(z.enum(CRITICIDADES)),
  tecnicoId: listaDe(uuid),
  tipoTrabajo: listaDe(z.enum(TIPOS_TRABAJO_REPORTE)),
});
export type FiltroReporte = z.infer<typeof filtroReporteSchema>;

/** Secciones que el informe PDF puede incluir o dejar fuera. */
export const SECCIONES_INFORME = ['resumen', 'ordenes', 'preventivo', 'costos', 'fallas'] as const;
export type SeccionInformeClave = (typeof SECCIONES_INFORME)[number];

export const informeReporteSchema = filtroReporteSchema.extend({
  /** Claves separadas por coma. Vacío o ausente = todas. */
  secciones: z.string().optional(),
  /** Compara cada indicador con el periodo anterior de igual duración. */
  comparar: z.coerce.boolean().optional(),
});
export type InformeReporteFiltro = z.infer<typeof informeReporteSchema>;

// ─── Sujetos evaluables (§12.7) ───────────────────────────────────────────────

export const sujetoEvaluableSchema = z.object({
  nombreCompleto: texto(200),
  rolId: uuid,
  ubicacionId: uuid.nullish(),
  usuarioId: uuid.nullish(),
  activo: z.boolean().default(true),
});

// ─── Catálogos (§3.2) ─────────────────────────────────────────────────────────

export const catalogoItemSchema = z.object({
  catalogo: z.enum(CATALOGOS),
  nombre: texto(200),
  descripcion: textoOpcional(2000),
  padreId: uuid.nullish(),
});

// ─── Tipos de falla (§7.3) ────────────────────────────────────────────────────

export const tipoFallaSchema = z.object({
  nombre: texto(200),
  descripcion: textoOpcional(2000),
  familiaId: uuid.nullish(),
  activo: z.boolean().default(true),
});

// ─── Campos personalizados (§16.7) ────────────────────────────────────────────

export const campoPersonalizadoSchema = z
  .object({
    entidad: z.enum(ENTIDADES_EXTENSIBLES),
    nombre: texto(150),
    tipo: z.enum(TIPOS_CAMPO_PERSONALIZADO),
    opciones: z.array(z.string().trim().min(1).max(150)).max(100).default([]),
    requerido: z.boolean().default(false),
    orden: z.number().int().min(0).default(0),
  })
  .refine((v) => !['SELECCION', 'SELECCION_MULTIPLE'].includes(v.tipo) || v.opciones.length > 0, {
    message: 'Un campo de selección necesita al menos una opción',
    path: ['opciones'],
  });

// ─── Usuarios y grupos (§12.4) ────────────────────────────────────────────────

export const invitarUsuarioSchema = z
  .object({
    nombreCompleto: texto(200),
    email: z.string().trim().toLowerCase().email('Correo inválido').optional(),
    telefonoMovil: z
      .string()
      .trim()
      .regex(/^[0-9+\s-]{6,20}$/, 'Teléfono inválido')
      .optional(),
    tipoAcceso: z.enum(TIPOS_ACCESO),
    /** Precarga la sede en la solicitud y restringe los activos (§12.6). */
    ubicacionAsignadaId: uuid.nullish(),
    grupos: z.array(uuid).max(50).default([]),
    /**
     * Cuenta usada por varias personas (§ pedido), p. ej. la cuenta de una
     * sede. Al crear una solicitud desde esta cuenta se exige el nombre de
     * quien la hace, para distinguir quién reportó qué.
     */
    esCompartido: z.boolean().default(false),
  })
  // §12.4: el contacto es la vía de invitación; sin uno de los dos no hay alta.
  .refine((v) => !!v.email || !!v.telefonoMovil, {
    message: 'Se requiere correo o teléfono móvil',
    path: ['email'],
  });
export type InvitarUsuarioEntrada = z.infer<typeof invitarUsuarioSchema>;

export const actualizarUsuarioSchema = invitarUsuarioSchema.innerType().partial().extend({
  activo: z.boolean().optional(),
});

export const grupoSchema = z.object({
  nombre: texto(150),
  descripcion: textoOpcional(2000),
  tipo: z.enum(TIPOS_GRUPO).default('FUNCIONAL'),
  administradorId: uuid.nullish(),
  miembros: z.array(uuid).max(500).default([]),
  /** Miembros con acceso a las listas de materiales y a los materiales de cada OT. */
  accesoMateriales: z.boolean().default(false),
  /** Confirma explícitamente un grupo de un solo miembro (§12.6). */
  confirmarMiembroUnico: z.boolean().default(false),
  /**
   * Tipos de activo visibles para este grupo (§ pedido: restringir qué
   * activos ve cada equipo). Vacío = sin restricción propia: el grupo no
   * limita nada, se apoya en que el tipo no tenga ningún grupo asignado.
   */
  tiposActivoId: z.array(uuid).max(200).default([]),
  /**
   * Procedimientos a cargo de este grupo (contraparte de "Grupos a cargo" en
   * la plantilla, § pedido): se guarda en la misma etiqueta GRUPO de
   * `configuracion_plantilla`, solo que editada desde el grupo en vez de
   * desde el procedimiento.
   */
  procedimientosId: z.array(uuid).max(500).default([]),
});

// ─── Solicitudes (§9.3) ───────────────────────────────────────────────────────

export const solicitudSchema = z.object({
  titulo: texto(250),
  descripcion: texto(4000),
  ubicacionId: uuid.nullish(),
  activoId: uuid.nullish(),
  prioridad: z.enum(PRIORIDADES).default('NINGUNA'),
  adjuntos: z.array(uuid).max(20).default([]),
  /**
   * Nombre de quien hace la solicitud (§ pedido), obligatorio cuando la
   * cuenta que la crea es compartida (`usuarios.es_compartido`); el servicio
   * lo exige en ese caso porque el esquema no conoce quién llama.
   */
  nombrePersona: textoOpcional(200),
  /** Apellidos de quien hace la solicitud: van con `nombrePersona` en una cuenta compartida. */
  apellidosPersona: textoOpcional(200),
});
export type SolicitudEntrada = z.infer<typeof solicitudSchema>;

/**
 * Solicitud que llega desde el QR de un equipo, sin sesión (`/s`). Mismo cuerpo
 * que el formulario interno; la única diferencia es que el nombre de quien
 * reporta es obligatorio, porque la cuenta que la firma es de sistema.
 */
export const solicitudPublicaSchema = solicitudSchema.extend({
  nombrePersona: texto(200),
  apellidosPersona: texto(200),
});
export type SolicitudPublicaEntrada = z.infer<typeof solicitudPublicaSchema>;

/**
 * Solicitud desde una tablet emparejada a una cuenta de sede. El nombre de la persona y el
 * dispositivo vienen del emparejamiento, no del cuerpo; por eso no se aceptan aquí.
 */
export const solicitudMovilSchema = solicitudSchema.omit({ nombrePersona: true, apellidosPersona: true });
export type SolicitudMovilEntrada = z.infer<typeof solicitudMovilSchema>;

/** Código de emparejamiento y datos del dispositivo al emparejarse por primera vez. */
export const emparejarDispositivoSchema = z.object({
  codigo: texto(200),
  nombreDispositivo: texto(120),
  nombrePersona: texto(200),
  /** Apellidos de la persona: obligatorios para identificar a quien usa la tablet. */
  apellidosPersona: texto(200),
  /** Identificador de la tablet, generado y guardado por la propia app; agrupa su historial. */
  tabletId: z.string().uuid(),
});
export type EmparejarDispositivoEntrada = z.infer<typeof emparejarDispositivoSchema>;

export const aprobarSolicitudSchema = z.object({
  /** El aprobador puede corregir el contexto antes de generar la OT (§9.2). */
  ubicacionId: uuid.nullish(),
  activoId: uuid.nullish(),
  prioridad: z.enum(PRIORIDADES).optional(),
  asignados: z.array(uuid).max(50).default([]),
  fechaVencimiento: fechaLiteral.nullish(),
  /**
   * Campos del formulario completo de OT (mismo que `/ordenes/nueva`), para
   * cuando aprobar no se limita a ubicación/activo/prioridad sino que abre el
   * alta completa de la orden — tipo de trabajo, recurrencia, procedimientos,
   * tiempo estimado, etc. Todos opcionales: sin ellos, `aprobar()` sigue
   * generando la OT reactiva simple de siempre.
   */
  titulo: texto(250).optional(),
  descripcion: textoOpcional(8000),
  tipoTrabajo: z.enum(TIPOS_TRABAJO).optional(),
  tiposFalla: z.array(uuid).max(20).optional(),
  asignadosGrupos: z.array(uuid).max(50).optional(),
  encargadoCategoriaId: uuid.nullish(),
  tiempoEstimadoMinutos: z.number().int().min(0).max(100_000).nullish(),
  fechaInicio: fechaLiteral.nullish(),
  recurrencia: z.enum(RECURRENCIAS).optional(),
  adjuntos: z.array(uuid).max(50).optional(),
  /**
   * Materiales/herramientas que el creador desmarcó de la lista previsualizada
   * (por tipo de activo + tipo de trabajo) antes de generar la OT. Si no viene
   * (`undefined`), la OT no se genera aquí: queda para la generación diferida
   * de siempre, al primer abrir el detalle.
   */
  materialesExcluidos: z.array(uuid).max(200).optional(),
});

export const declinarSolicitudSchema = z.object({
  /** §9.4: la declinación exige motivo, para cerrar el ciclo con el solicitante. */
  motivo: texto(2000),
});

// ─── Órdenes de trabajo (§10.3) ───────────────────────────────────────────────

/**
 * Último día elegible para la fecha de inicio de una OT: hoy en Lima más un año.
 * Comparación de texto, igual que el resto de fechas `AAAA-MM-DD` de este archivo.
 */
export function fechaInicioMaxima(): string {
  const hoy = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Lima' }).format(new Date());
  return `${Number(hoy.slice(0, 4)) + 1}${hoy.slice(4)}`;
}

export const ordenTrabajoSchema = z.object({
  titulo: texto(250),
  descripcion: textoOpcional(8000),
  prioridad: z.enum(PRIORIDADES).default('NINGUNA'),
  tipoTrabajo: z.enum(TIPOS_TRABAJO).default('REACTIVA'),
  ubicacionId: uuid.nullish(),
  activoId: uuid.nullish(),
  sujetoEvaluableId: uuid.nullish(),
  tiposFalla: z.array(uuid).max(20).default([]),
  asignadosUsuarios: z.array(uuid).max(50).default([]),
  asignadosGrupos: z.array(uuid).max(50).default([]),
  encargadoCategoriaId: uuid.nullish(),
  tiempoEstimadoMinutos: z.number().int().min(0).max(100_000).nullish(),
  fechaInicio: fechaLiteral.nullish(),
  fechaVencimiento: fechaLiteral.nullish(),
  recurrencia: z.enum(RECURRENCIAS).default('NO_SE_REPITE'),
  recurrenciaConfig: z.record(z.unknown()).nullish(),
  /**
   * Plantilla de OT recurrente (§ pedido): en vez de esperar a que se
   * complete la OT actual para recién ahí crear la siguiente (eso ya existía,
   * ver `OrdenesService.generarSiguienteOcurrencia`), una fila marcada acá
   * nunca se trabaja — el cron diario la usa como fuente y genera la
   * siguiente ocurrencia sola según `fechaVencimiento` + `recurrencia`,
   * leyendo lo que la plantilla tenga editado en ese momento (asignados,
   * etc.). `recurrenciaActiva` es el interruptor para pausarla sin borrarla.
   */
  esPlantillaRecurrente: z.boolean().default(false),
  recurrenciaActiva: z.boolean().default(true),
  ordenTrabajoPadreId: uuid.nullish(),
  adjuntos: z.array(uuid).max(50).default([]),
  /** Ver `aprobarSolicitudSchema.materialesExcluidos`. */
  materialesExcluidos: z.array(uuid).max(200).optional(),
}).refine((datos) => !datos.fechaInicio || !datos.fechaVencimiento || datos.fechaInicio <= datos.fechaVencimiento, {
  // Comparación de texto, no de Date: ambas son literales AAAA-MM-DD de ancho
  // fijo, así que el orden lexicográfico ya es el orden cronológico (§ bug
  // reportado: antes se podía guardar una OT con inicio posterior al
  // vencimiento, produciendo una barra de duración negativa en el Gantt).
  message: 'La fecha de inicio no puede ser posterior a la de vencimiento.',
  path: ['fechaInicio'],
}).refine((datos) => !(datos.activoId && datos.sujetoEvaluableId), {
  // Una OT es sobre un equipo o sobre una persona, nunca sobre ambos.
  message: 'Una orden es sobre un activo o sobre una persona, no sobre ambos.',
  path: ['sujetoEvaluableId'],
}).refine((datos) => !datos.fechaInicio || datos.fechaInicio <= fechaInicioMaxima(), {
  message: 'La fecha de inicio no puede ser más de un año después de hoy.',
  path: ['fechaInicio'],
});
export type OrdenTrabajoEntrada = z.infer<typeof ordenTrabajoSchema>;

export const cambiarEstadoOtSchema = z.object({
  estado: z.enum(ESTADOS_OT),
  nota: textoOpcional(2000),
});

/**
 * Sub-órdenes multi-activo: una OT "padre" reparte su alcance en una OT hija
 * por cada activo elegido, cada una con su propio procedimiento opcional.
 * Ejemplo: 10 activos de una ubicación, cada uno con su propia OT pero todas
 * agrupadas bajo la misma orden padre (mismo mecanismo que `reasignar`).
 */
export const crearSubOrdenesSchema = z.object({
  items: z
    .array(
      z.object({
        // Cada sub-orden es sobre un activo o sobre una persona, nunca ambos.
        activoId: uuid.nullish(),
        sujetoEvaluableId: uuid.nullish(),
        procedimientoPlantillaId: uuid.nullish(),
        // Overrides por sub-orden (§ pedido): sin esto, cada hija heredaba
        // ciegamente descripción/asignados/encargado/fechas de la orden padre
        // y nunca tenía tiempo estimado ni fotos propias. Si no vienen, se
        // mantiene el comportamiento anterior (hereda del padre / queda null).
        descripcion: textoOpcional(8000),
        ubicacionId: uuid.nullish(),
        prioridad: z.enum(PRIORIDADES).optional(),
        tipoTrabajo: z.enum(TIPOS_TRABAJO).optional(),
        tiposFalla: z.array(uuid).max(20).optional(),
        asignadosUsuarios: z.array(uuid).max(50).optional(),
        asignadosGrupos: z.array(uuid).max(50).optional(),
        encargadoCategoriaId: uuid.nullish(),
        tiempoEstimadoMinutos: z.number().int().min(0).max(100_000).nullish(),
        fechaInicio: fechaLiteral.nullish(),
        fechaVencimiento: fechaLiteral.nullish(),
        adjuntos: z.array(uuid).max(50).optional(),
      }).refine((item) => Number(item.activoId != null) + Number(item.sujetoEvaluableId != null) === 1, {
        message: 'Cada sub-orden va sobre un activo o sobre una persona, no ambos ni ninguno.',
      }),
    )
    .min(1)
    .max(200),
});
export type CrearSubOrdenesEntrada = z.infer<typeof crearSubOrdenesSchema>;

/**
 * Solo el vencimiento: lo que usa arrastrar-y-soltar en el calendario (§3).
 *
 * A propósito NO se usa `z.coerce.date()`: convertir a un objeto `Date` de JS
 * y de vuelta introduce una conversión de huso horario (el `date` de
 * Postgres se sirializa con la hora local del proceso) que corre la fecha un
 * día — el texto llega tal cual del selector de fecha y Postgres lo toma
 * como literal, sin pasar por ningún reloj.
 */
export const reprogramarOtSchema = z.object({
  fechaVencimiento: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida (use AAAA-MM-DD)'),
});

/** Mismo mecanismo que `reprogramarOtSchema`, para el borde izquierdo de la
 * barra en el Gantt (§ pedido): ajustar solo la fecha de inicio. */
export const reprogramarInicioOtSchema = z.object({
  fechaInicio: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida (use AAAA-MM-DD)')
    .nullable(),
});

export const comentarioSchema = z.object({
  texto: texto(8000),
  adjuntos: z.array(uuid).max(20).default([]),
  /** Menciones que disparan notificación (§15.2). */
  menciones: z.array(uuid).max(50).default([]),
});

export const filtroOtSchema = paginacionSchema.extend({
  estado: z.array(z.enum(ESTADOS_OT)).optional(),
  prioridad: z.array(z.enum(PRIORIDADES)).optional(),
  tipoTrabajo: z.array(z.enum(TIPOS_TRABAJO)).optional(),
  ubicacionId: uuid.optional(),
  activoId: uuid.optional(),
  /** OT asignadas a una persona (ficha de personal). */
  sujetoEvaluableId: uuid.optional(),
  /** OT cuyo activo tiene alguno de estos tipos (tabla de OT del dashboard). */
  tipoActivoId: z.array(uuid).optional(),
  asignadoId: uuid.optional(),
  /** Solo las OT asignadas a este grupo (sin contar a sus personas por separado). */
  grupoId: uuid.optional(),
  tipoFallaId: uuid.optional(),
  /** Correctivas generadas por una inspección puntual (§8.9, punto 30). */
  otOrigenId: uuid.optional(),
  /** Sub-órdenes de una OT padre (reasignación o reparto multi-activo). */
  ordenTrabajoPadreId: uuid.optional(),
  /** La lista principal (§ bug reportado) no debe mezclar sub-órdenes sueltas entre las OT raíz —
   * eso descuadra la paginación, porque el total cuenta padres e hijas pero
   * el cliente descarta las hijas al pintar. El Gantt y otras vistas que sí
   * quieren ver ambas en una sola lista (para agruparlas ellas mismas) no
   * mandan este filtro. */
  soloRaiz: z.coerce.boolean().optional(),
  encargadoCategoriaId: uuid.optional(),
  recurrencia: z.enum(RECURRENCIAS).optional(),
  /** Sin especificar, la lista normal nunca muestra plantillas recurrentes
   * (§ pedido) — la pantalla que las administra pasa `true` a propósito. */
  esPlantillaRecurrente: z.coerce.boolean().optional(),
  /** Solo OT con alguna recurrencia (≠ NO_SE_REPITE): lo usan el calendario y
   * el Gantt para planificar. Se manda únicamente como `true`; un string
   * vacío se descarta antes de llegar acá. */
  soloRecurrentes: z.coerce.boolean().optional(),
  /** Solo OT padre: las que tienen sub-órdenes. Lo usan la lista, el calendario y el Gantt. */
  soloPadres: z.coerce.boolean().optional(),
  vencidas: z.coerce.boolean().optional(),
  // Rango sobre fecha_vencimiento: lo que usa la vista de calendario para
  // pedir solo las OT del mes visible, en vez de depender de la paginación.
  fechaDesde: z.coerce.date().optional(),
  fechaHasta: z.coerce.date().optional(),
  fechaInicioDesde: z.coerce.date().optional(),
  fechaInicioHasta: z.coerce.date().optional(),
  // Día de creación de la OT (no la de inicio ni la de vencimiento), en fecha de Lima.
  creadoDesde: fechaLiteral.optional(),
  creadoHasta: fechaLiteral.optional(),
  tiempoEstimadoDesde: z.coerce.number().int().nonnegative().optional(),
  tiempoEstimadoHasta: z.coerce.number().int().nonnegative().optional(),
});

export const filtroSolicitudSchema = paginacionSchema.extend({
  estado: z.array(z.enum(ESTADOS_SOLICITUD)).optional(),
  ubicacionId: uuid.optional(),
  activoId: uuid.optional(),
  /** Solicitudes cuyo activo tiene alguno de estos tipos (dashboard). */
  tipoActivoId: z.array(uuid).optional(),
  prioridad: z.array(z.enum(PRIORIDADES)).optional(),
  buscar: z.string().trim().max(200).optional(),
});

/** Tipos de activo que el usuario quiere ver en «Solicitudes por tipo» del dashboard. */
export const preferenciaTiposSolicitudSchema = z.object({
  tipoActivoId: z.array(uuid).max(200),
});
export type PreferenciaTiposSolicitud = z.infer<typeof preferenciaTiposSolicitudSchema>;

export const filtroActivoSchema = paginacionSchema.extend({
  estado: z.array(z.enum(ESTADOS_ACTIVO)).optional(),
  criticidad: z.array(z.enum(CRITICIDADES)).optional(),
  // Se suman dentro de cada filtro (cualquiera de las ubicaciones/fabricantes/
  // tipos elegidos) y los filtros se combinan entre sí (§ pedido).
  ubicacionId: z.array(uuid).optional(),
  tipoActivoId: z.array(uuid).optional(),
  motivoInactividadId: uuid.optional(),
  fabricanteId: z.array(uuid).optional(),
  modeloId: uuid.optional(),
  /** Solo activos cuya garantía ya venció (expiracionGarantia < hoy). */
  garantiaVencida: z.coerce.boolean().optional(),
  /** Los ocultos («no eliminados, pero fuera de vista») se excluyen salvo que se pida explícitamente. */
  incluirOcultos: z.coerce.boolean().optional(),
  incluirMomentaneos: z.coerce.boolean().optional(),
});

/**
 * Exportación de datos (Excel, CSV, PDF). Cada módulo que exporta reutiliza
 * su mismo filtro de listado, sin paginar: la exportación trae la vista completa.
 */
export const FORMATOS_EXPORTACION = ['xlsx', 'csv', 'pdf'] as const;
export type FormatoExportacion = (typeof FORMATOS_EXPORTACION)[number];

export const formatoExportacionSchema = z.enum(FORMATOS_EXPORTACION);

export const exportarActivosFiltroSchema = filtroActivoSchema.omit({ pagina: true, tamano: true });

// ─── Licencias ────────────────────────────────────────────────────────────────

export const ajustarAsientosSchema = z.object({
  asientosContratados: z.number().int().min(1).max(10_000),
});

export interface ResumenLicencia {
  plan: string;
  estado: string;
  precioAsientoMensualPen: string;
  asientosContratados: number;
  asientosConsumidos: number;
  asientosDisponibles: number;
  desglose: { tipoAcceso: string; usuarios: number; facturable: boolean }[];
  costoMensualPen: string;
  igvPen: string;
  totalMensualPen: string;
  periodoInicio: string | null;
  periodoFin: string | null;
}

// ─── Inventario de partes (§6) ────────────────────────────────────────────────

export const parteSchema = z.object({
  nombre: texto(250),
  // §6.3: obligatorio en la plataforma. Es la identidad física de la pieza, la
  // que el técnico lee en el estante.
  idPieza: texto(60),
  numeroParte: textoOpcional(100),
  descripcion: textoOpcional(4000),
  costoUnitarioPen: z.coerce.number().min(0).max(9_999_999).default(0),
  tiposParte: z.array(uuid).max(20).default([]),
  // §6.2 y §6.3 modelan la compatibilidad de dos maneras distintas y se
  // conservan las dos: el tipo es la regla general, el activo la excepción.
  tiposActivoCompatibles: z.array(uuid).max(50).default([]),
  activosCompatibles: z.array(uuid).max(200).default([]),
  gruposACargo: z.array(uuid).max(20).default([]),
  codigoQr: textoOpcional(40),
});
export type ParteEntrada = z.infer<typeof parteSchema>;

/**
 * Umbrales de una parte en una ubicación (§6.4).
 *
 * `unidadesEnExistencia` no viaja a propósito: es un valor derivado de las
 * transacciones y el rol de aplicación ni siquiera tiene UPDATE sobre esa
 * columna. Aceptarlo aquí sería ofrecer una edición que la base va a rechazar.
 */
export const umbralesStockSchema = z
  .object({
    area: textoOpcional(120),
    minimoEnExistencia: z.coerce.number().int().min(0).max(1_000_000).default(0),
    maximoEnExistencia: z.coerce.number().int().min(0).max(1_000_000).nullish(),
  })
  .refine((v) => v.maximoEnExistencia == null || v.maximoEnExistencia >= v.minimoEnExistencia, {
    message: 'El máximo no puede ser menor que el mínimo',
    path: ['maximoEnExistencia'],
  });
export type UmbralesStockEntrada = z.infer<typeof umbralesStockSchema>;

const cantidad = z.coerce
  .number()
  .int('La cantidad debe ser un número entero de unidades')
  .min(-1_000_000)
  .max(1_000_000)
  .refine((v) => v !== 0, { message: 'La cantidad no puede ser cero' });

/**
 * Movimiento de existencias en una ubicación (§6.5).
 *
 * El signo lo impone también un CHECK en la base; se valida aquí para que el
 * usuario lea una frase en español y no un `check_violation`.
 */
export const transaccionParteSchema = z
  .object({
    tipo: z.enum(['ENTRADA', 'SALIDA', 'AJUSTE']),
    ubicacionId: uuid,
    cantidad,
    costoUnitarioPen: z.coerce.number().min(0).max(9_999_999).optional(),
    ordenTrabajoId: uuid.nullish(),
    nota: textoOpcional(1000),
  })
  .superRefine((v, ctx) => {
    if (v.tipo === 'ENTRADA' && v.cantidad < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cantidad'],
        message: 'Una entrada suma unidades: la cantidad debe ser positiva',
      });
    }
    if (v.tipo === 'SALIDA' && v.cantidad > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cantidad'],
        message: 'Una salida descuenta unidades: la cantidad debe ser negativa',
      });
    }
  });
export type TransaccionParteEntrada = z.infer<typeof transaccionParteSchema>;

/** §6.6: una transferencia genera dos transacciones espejo. */
export const transferenciaParteSchema = z
  .object({
    parteId: uuid,
    ubicacionOrigenId: uuid,
    ubicacionDestinoId: uuid,
    cantidad: z.coerce.number().int().min(1).max(1_000_000),
    nota: textoOpcional(1000),
  })
  .refine((v) => v.ubicacionOrigenId !== v.ubicacionDestinoId, {
    message: 'El origen y el destino deben ser ubicaciones distintas',
    path: ['ubicacionDestinoId'],
  });
export type TransferenciaParteEntrada = z.infer<typeof transferenciaParteSchema>;

/**
 * Consumo de partes desde una orden de trabajo (§6.2, §17.4).
 *
 * La ubicación viaja explícita aunque §6.6 diga que se propone la de la OT: el
 * técnico puede tomar la pieza del almacén central, y la transacción tiene que
 * decir de dónde salió de verdad.
 */
export const consumoParteSchema = z.object({
  parteId: uuid,
  ubicacionId: uuid,
  cantidad: z.coerce.number().int().min(1).max(1_000_000),
  nota: textoOpcional(1000),
});
export type ConsumoParteEntrada = z.infer<typeof consumoParteSchema>;

export const filtroParteSchema = paginacionSchema.extend({
  tipoParteId: uuid.optional(),
  ubicacionId: uuid.optional(),
  activoCompatibleId: uuid.optional(),
  tipoActivoCompatibleId: uuid.optional(),
  grupoId: uuid.optional(),
  /** §6.2: el filtro Necesita Reposición, evaluado por ubicación. */
  necesitaReposicion: z.coerce.boolean().optional(),
});
export type FiltroParte = z.infer<typeof filtroParteSchema>;

export const filtroTransaccionSchema = paginacionSchema.extend({
  ubicacionId: uuid.optional(),
  tipo: z.enum(['ENTRADA', 'SALIDA', 'AJUSTE', 'TRANSFERENCIA']).optional(),
});

/** Personal evaluable (§12.7): el esquema de `sujetoEvaluableSchema` (arriba). */
export type SujetoEvaluableEntrada = z.infer<typeof sujetoEvaluableSchema>;

export const filtroSujetoEvaluableSchema = paginacionSchema.extend({
  buscar: z.string().trim().max(200).optional(),
  activo: z.enum(['true', 'false']).optional(),
  rolId: uuid.optional(),
  /** La sede o ubicación elegida incluye todas sus sub-ubicaciones. */
  ubicacionId: uuid.optional(),
});
export type FiltroSujetoEvaluable = z.infer<typeof filtroSujetoEvaluableSchema>;

export interface SujetoEvaluable {
  id: string;
  nombreCompleto: string;
  rolId: string;
  rol: string;
  ubicacionId: string | null;
  ubicacion: string | null;
  activo: boolean;
  creadoEl: string;
}

// ─── Listas de materiales y herramientas (§ herramientas) ───────────────────

export const TIPOS_LINEA_MATERIAL = ['MATERIAL', 'HERRAMIENTA'] as const;
export type TipoLineaMaterial = (typeof TIPOS_LINEA_MATERIAL)[number];

export const lineaMaterialSchema = z.object({
  nombre: texto(200),
  tipo: z.enum(TIPOS_LINEA_MATERIAL),
  cantidad: z.number().positive().max(100_000),
  nota: textoOpcional(500).nullish(),
});
export type LineaMaterialEntrada = z.infer<typeof lineaMaterialSchema>;

/** Reemplaza todas las líneas de una lista (general o de un tipo de trabajo). */
export const listaMaterialesSchema = z.object({
  tipoTrabajo: z.enum(TIPOS_TRABAJO).nullish(),
  lineas: z.array(lineaMaterialSchema).max(200),
});
export type ListaMaterialesEntrada = z.infer<typeof listaMaterialesSchema>;

/**
 * Inventario de activos: un inventario cubre varias sedes y se verifica en
 * revisiones (pasadas) que pueden ser de días distintos.
 */
export const inventarioSchema = z.object({
  nombre: texto(200),
  descripcion: textoOpcional(2000),
  sedeIds: z.array(uuid).min(1, 'Elija al menos una sede').max(200),
});
export type InventarioEntrada = z.infer<typeof inventarioSchema>;

export const revisionInventarioSchema = z.object({
  nombre: texto(200),
  fecha: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida (use AAAA-MM-DD)')
    .optional(),
  nota: textoOpcional(2000),
});
export type RevisionInventarioEntrada = z.infer<typeof revisionInventarioSchema>;

export const registrarActivosInventarioSchema = z.object({
  activoIds: z.array(uuid).min(1, 'Elija al menos un activo').max(500),
  responsableId: uuid,
  nota: textoOpcional(1000),
});
export type RegistrarActivosInventarioEntrada = z.infer<typeof registrarActivosInventarioSchema>;

export const marcarMaterialOtSchema = z.object({
  marcado: z.boolean(),
});

// ─── Catálogo de materiales y plan por solicitud (0064) ─────────────────────

export const materialCatalogoSchema = z.object({
  nombre: texto(200),
  tipo: z.enum(TIPOS_LINEA_MATERIAL),
  /** Opcional: sin tipo de activo, el elemento se muestra a todas las solicitudes. */
  tipoActivoId: uuid.nullish(),
});
export type MaterialCatalogoEntrada = z.infer<typeof materialCatalogoSchema>;

/** Plan de materiales de una solicitud. Si la línea viene del catálogo, el servidor toma nombre y tipo de ahí. */
export const planSolicitudMaterialesSchema = z.object({
  lineas: z
    .array(
      z.object({
        catalogoId: uuid.nullish(),
        nombre: texto(200),
        tipo: z.enum(TIPOS_LINEA_MATERIAL),
        cantidad: z.number().positive().max(100_000),
        nota: textoOpcional(500).nullish(),
      }),
    )
    .max(200),
});
export type PlanSolicitudMaterialesEntrada = z.infer<typeof planSolicitudMaterialesSchema>;
export type MarcarMaterialOtEntrada = z.infer<typeof marcarMaterialOtSchema>;
